import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtSf } from "../lib/utils";
import { toast } from "sonner";
import DateFilter from "./layout/DateFilter";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

// Always display SF as full integer with thousand separators (e.g. "2,000" not "2K")
const fmtK = fmtSf;

const ACCENT = "#EC4899"; // pink/magenta accent like the mockup

export function MoldManufacturedCard({ showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/departments/MOLD/manufactured", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden p-5" data-testid="mold-manufactured-card">
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">Mold Manufactured</div>
      <div className="mb-4">
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-heading font-bold text-4xl tabular-nums">
            {loading ? "—" : `${fmtK(data?.completed_sf)} SF`}
          </div>
        </div>
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
          Completed
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div>
          <div className="text-muted-foreground/80">{loading ? "—" : `${fmtK(data?.total_sf)} SF`}</div>
        </div>
        <div className="text-right text-muted-foreground/80">
          {loading ? "—" : `Total (${data?.percent}%)`}
        </div>
        <div className="text-muted-foreground/80">
          {loading ? "—" : `${data?.completed_pieces}/${data?.total_pieces} pcs`}
        </div>
        <div className="text-right text-muted-foreground/80">
          {loading ? "—" : `${data?.hours?.toFixed(2)} hours`}
        </div>
      </div>

      <div className="border border-border/60 px-5 py-6 text-center" data-testid="mold-manufactured-sfh">
        <div className="font-heading font-bold text-4xl tabular-nums" style={{ color: "#fff" }}>
          {loading ? "—" : data?.sf_per_hour}
        </div>
        <div className="text-[11px] text-muted-foreground mt-1 tracking-widest uppercase">
          SF / Hour (Completed)
        </div>
      </div>
    </section>
  );
}

export function MoldManufacturedTrendCard({ showMexico = false }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/departments/MOLD/manufactured", { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params]);

  const trend = data?.daily_trend || [];

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden p-5" data-testid="mold-manufactured-trend-card">
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT}66 100%)` }} />

      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-bold text-lg">Mold Manufactured · Daily SF/Hour</h3>
        <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{trend.length} days</div>
      </div>
      <div className="mb-4">
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      {loading && <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">Loading…</div>}
      {!loading && trend.length === 0 && (
        <div className="h-[320px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">No data in range</div>
      )}
      {!loading && trend.length > 0 && (
        <div className="h-[320px]" data-testid="mold-trend-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart data={trend} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 10 }}
                     tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                labelStyle={{ color: "#94A3B8" }}
                formatter={(v, k) => {
                  if (k === "sf_per_hour") return [`${v} SF/hr`, "SF / Hour"];
                  if (k === "sf") return [`${v} SF`, "Completed SF"];
                  if (k === "hours") return [`${v} hrs`, "Mold Hours"];
                  return [v, k];
                }}
              />
              <Line type="monotone" dataKey="sf_per_hour" stroke={ACCENT} strokeWidth={2}
                    dot={false} activeDot={{ r: 4, fill: ACCENT }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

export function MoldEfficiencyTrendCard({ showMexico = false, dept = "MOLD" }) {
  const [filter, setFilter] = useState({ preset: "all" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => {
    const p = { preset: filter.preset, show_mexico: showMexico };
    if (filter.preset === "custom") { p.start = filter.start; p.end = filter.end; }
    return p;
  }, [filter, showMexico]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // MOLD uses the dedicated QC-based manufactured endpoint; other depts use the
    // generic efficiency-trend endpoint that computes from Production + Hours + Materials.
    const url = dept === "MOLD"
      ? "/departments/MOLD/manufactured"
      : `/departments/${dept}/efficiency-trend`;
    api.get(url, { params })
      .then((r) => !cancelled && setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [params, dept]);

  const trend = data?.daily_trend || [];

  return (
    <section className="relative bg-[#12141D] border border-border overflow-hidden p-5" data-testid="mold-efficiency-trend-card">
      <span className="absolute top-0 left-0 right-0 h-[2px]"
            style={{ background: "linear-gradient(90deg, #F59E0B 0%, #EC4899 100%)" }} />

      <div className="flex items-center justify-between mb-1">
        <h3 className="font-heading font-bold text-xl">Efficiency Trend</h3>
        <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{trend.length} days</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">SF/Hr and Cost/SF over time</p>

      <div className="mb-4">
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      {loading && <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">Loading…</div>}
      {!loading && trend.length === 0 && (
        <div className="h-[360px] flex items-center justify-center text-sm text-muted-foreground uppercase tracking-widest">No data in range</div>
      )}
      {!loading && trend.length > 0 && (
        <div className="h-[360px]" data-testid="mold-efficiency-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
              <CartesianGrid stroke="#1F2937" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 10 }}
                     tickFormatter={(v) => v?.slice(5)} />
              <YAxis yAxisId="left" stroke="#EC4899" tick={{ fontSize: 10 }}
                     label={{ value: "SF/Hr", angle: -90, position: "insideLeft", fill: "#EC4899", fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" tick={{ fontSize: 10 }}
                     label={{ value: "Cost/SF", angle: 90, position: "insideRight", fill: "#F59E0B", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ backgroundColor: "#0A0B10", border: "1px solid #1F2937", fontSize: 12 }}
                labelStyle={{ color: "#94A3B8" }}
                formatter={(v, k) => {
                  if (k === "sf_per_hour") return [`${v} SF/hr`, "SF/Hr"];
                  if (k === "cost_per_sf") return [`$${v}/SF`, "Cost/SF"];
                  return [v, k];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }}
                      formatter={(value) => value === "sf_per_hour" ? "SF/Hr" : "Cost/SF"} />
              <Line yAxisId="left" type="monotone" dataKey="sf_per_hour" stroke="#EC4899" strokeWidth={2}
                    dot={false} activeDot={{ r: 4 }} />
              <Line yAxisId="right" type="monotone" dataKey="cost_per_sf" stroke="#F59E0B" strokeWidth={2}
                    dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
